import { Controller, ForbiddenException, Get, Query, Req, UseGuards } from '@nestjs/common';
import { ApiBearerAuth, ApiOperation, ApiSecurity, ApiTags } from '@nestjs/swagger';
import { AdminAccessGuard } from '../auth/admin-access.guard';
import { AuthGuard, AuthenticatedRequest } from '../auth/auth.guard';
import { AnalyticsQuery, MerchantAnalyticsService } from './merchant-analytics.service';

/** Roles that may read their own store's analytics. */
const CAN_VIEW = new Set([
  'store_owner', 'merchant', 'store_manager', 'accountant',
  'admin', 'super_admin', 'platform_admin', 'account_manager', 'operator',
]);

/**
 * Store-scoped analytics.
 *
 * AdminAccessGuard has already proven this user is active staff of the store in
 * context, so cross-tenant access is settled before anything here runs.
 */
@ApiTags('Merchant · Analytics')
@ApiBearerAuth()
@ApiSecurity('store-id')
@Controller('merchant/analytics')
@UseGuards(AuthGuard, AdminAccessGuard)
export class MerchantAnalyticsController {
  constructor(private readonly analytics: MerchantAnalyticsService) {}

  private assert(request: AuthenticatedRequest): void {
    if (!request.user || !CAN_VIEW.has(request.user.role)) {
      throw new ForbiddenException('You do not have access to analytics.');
    }
  }

  @Get('summary')
  summary(@Query() query: AnalyticsQuery, @Req() request: AuthenticatedRequest) {
    this.assert(request);
    return this.analytics.summary(query);
  }

  @Get('funnel')
  @ApiOperation({ summary: 'View → cart → checkout → purchase, counted in sessions' })
  funnel(@Query() query: AnalyticsQuery, @Req() request: AuthenticatedRequest) {
    this.assert(request);
    return this.analytics.funnel(query);
  }

  @Get('products')
  @ApiOperation({ summary: 'Per-product views, adds, purchases and revenue' })
  products(@Query() query: AnalyticsQuery, @Req() request: AuthenticatedRequest) {
    this.assert(request);
    return this.analytics.products(query);
  }

  @Get('searches')
  @ApiOperation({ summary: 'What shoppers searched for — including what they did not find' })
  searches(@Query() query: AnalyticsQuery, @Req() request: AuthenticatedRequest) {
    this.assert(request);
    return this.analytics.searches(query);
  }

  @Get('trend')
  trend(@Query() query: AnalyticsQuery, @Req() request: AuthenticatedRequest) {
    this.assert(request);
    return this.analytics.trend(query);
  }
}
